import { useEffect, useState, useMemo } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { api } from '@/lib/api';
import HackCard from '@/components/HackCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import FilterChips, { SKIN_TYPES, CONCERNS } from '@/components/Filters';
import { useAuth } from '@/context/AuthContext';

export default function DiyLabPage() {
  const { user } = useAuth();
  const [hacks, setHacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [skinType, setSkinType] = useState(user?.skin_type || null);
  const [concern, setConcern] = useState(null);
  const [debouncedQ, setDebouncedQ] = useState('');
  const PAGE = 9;
  const [visible, setVisible] = useState(PAGE);

  // Reset visible page on filter change
  useEffect(() => { setVisible(PAGE); }, [debouncedQ, skinType, concern]);

  // debounce search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    if (user?.skin_type && !skinType) setSkinType(user.skin_type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.skin_type]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (debouncedQ) params.q = debouncedQ;
        if (skinType) params.skin_type = skinType;
        if (concern) params.concern = concern;
        const { data } = await api.get('/hacks', { params });
        if (!cancelled) setHacks(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, skinType, concern]);

  const activeChips = useMemo(() => {
    const chips = [];
    if (skinType) chips.push({ label: `Skin: ${skinType}`, clear: () => setSkinType(null) });
    if (concern) chips.push({ label: `Concern: ${concern.replace('-', ' ')}`, clear: () => setConcern(null) });
    if (q) chips.push({ label: `“${q}”`, clear: () => setQ('') });
    return chips;
  }, [skinType, concern, q]);

  return (
    <div data-testid="diy-lab-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold">DIY Beauty Lab</span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          Home-made hacks for every glow.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Search by ingredient (honey, turmeric, oat) or skin concern. Each hack lists exact ingredients,
          steps, and the benefits you can expect.
        </p>
      </div>

      <div className="mt-8 space-y-5">
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search hacks, ingredients, concerns..."
            className="pl-9 h-11 rounded-xl bg-card border-border/80"
            data-testid="diy-search-input"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-secondary"
              data-testid="diy-search-clear-button"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-6">
          <FilterChips
            label="Skin Type"
            value={skinType}
            onChange={setSkinType}
            options={SKIN_TYPES}
            testId="filter-skin-type-toggle-group"
          />
          <FilterChips
            label="Concern"
            value={concern}
            onChange={setConcern}
            options={CONCERNS}
            testId="filter-concern-toggle-group"
          />
        </div>

        {activeChips.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <SlidersHorizontal className="h-3 w-3" /> Active:
            </span>
            {activeChips.map((c, i) => (
              <button
                key={i}
                onClick={c.clear}
                className="text-xs rounded-full border border-border bg-card px-2.5 py-1 hover:bg-secondary inline-flex items-center gap-1"
              >
                {c.label}
                <X className="h-3 w-3" />
              </button>
            ))}
            <button
              onClick={() => {
                setSkinType(null);
                setConcern(null);
                setQ('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
              data-testid="diy-clear-all-filters"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      <div className="mt-10" data-testid="diy-results-grid">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[...Array(9)].map((_, i) => (
              <Skeleton key={i} className="h-80 rounded-2xl" data-testid="page-loading-skeleton" />
            ))}
          </div>
        ) : hacks.length === 0 ? (
          <div className="text-center py-16" data-testid="page-empty-state">
            <p className="font-display text-2xl text-ink">Nothing matches just yet.</p>
            <p className="text-sm text-muted-foreground mt-2">Try clearing a filter or searching a different ingredient.</p>
            <Button
              onClick={() => {
                setSkinType(null);
                setConcern(null);
                setQ('');
              }}
              variant="outline"
              className="mt-5 rounded-xl"
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
              Showing {Math.min(visible, hacks.length)} of {hacks.length} {hacks.length === 1 ? 'hack' : 'hacks'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {hacks.slice(0, visible).map((h) => (
                <HackCard key={h.id} hack={h} />
              ))}
            </div>
            {visible < hacks.length && (
              <div className="mt-8 text-center">
                <Button
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => setVisible((v) => v + PAGE)}
                  data-testid="diy-load-more-button"
                >
                  Show more hacks
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
